/**
 * Typesense service layer — uses raw fetch() to bypass webpack/axios
 * interop issues with the typesense-js client in the browser bundle.
 *
 * The Typesense HTTP API is simple and stable, so a small wrapper is
 * more reliable than the SDK in a React CRA build.
 */

import { COLLECTION_META, COLLECTION_INCLUDE_FIELDS, ROLE_GATED_COLLECTIONS } from "../config/typesense";
import { buildContactsAwareFilter, CMDK_SEARCH_OPTIONS } from "@officehub/shared";
import type { SearchResult, MultiSearchResult, SearchHit } from "../types/search";
import { SERVICE_ID_PATTERN } from './serviceIdPattern';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBaseUrl(): string {
  const host = process.env.REACT_APP_TYPESENSE_HOST;
  if (!host) throw new Error("REACT_APP_TYPESENSE_HOST is not set");
  const port = process.env.REACT_APP_TYPESENSE_PORT || "443";
  const protocol = process.env.REACT_APP_TYPESENSE_PROTOCOL || (port === "443" ? "https" : "http");
  const portSuffix = (protocol === "https" && port === "443") || (protocol === "http" && port === "80") ? "" : `:${port}`;
  return `${protocol}://${host}${portSuffix}`;
}

/*
 * #4031: the browser holds NO static Typesense key. Every search authenticates
 * with a per-user scoped key minted by search_getScopedSearchKey (org isolation
 * signed into the key). The old broad key (a build-time env var,
 * collections ["*"], readable from the login page) is gone from the build;
 * scripts/guards/check-no-browser-search-key.mjs keeps it that way.
 */

/**
 * Search params that switch Typesense highlighting completely off.
 *
 * Typesense's DEFAULT is to highlight every queried field, and both search paths
 * here used to go further and send `highlight_full_fields=<every queried field>`
 * — the most expensive setting available. That combination makes the server
 * return, for every hit, the ENTIRE value of each matching queried field with no
 * snippeting, and then return it a second time because `enable_highlight_v1`
 * defaults to true and emits the deprecated `highlights` array alongside the
 * newer `highlight` object.
 *
 * On the collections whose queried fields are long free text — `call_logs.callNotes`,
 * `support_cases.description`, `quotes.scopeOfWork`, `qbo_bills.privateNote`,
 * `price_book_items.description`, `service_parts_list.partDescription` — that
 * duplicated full text is the bulk of the response body, and the global Cmd+K
 * search asks ~20 collections for 5 hits each on every debounced keystroke.
 *
 * None of it was ever rendered: `SearchHit.highlights` has no consumer anywhere
 * in the client outside test fixtures (`mapHit` parses it and every caller drops
 * it). So the default is off, and a caller that genuinely wants highlighting opts
 * in per-request via `searchCollection`'s `highlightFields` — snippeted, never
 * whole-field.
 *
 * Param semantics are from the Typesense search API reference: `highlight_fields`
 * "set to `none` to disable snippetting fully"; `highlight_full_fields` "set to
 * `none` to disable highlighting fully"; `enable_highlight_v1` "flag for disabling
 * the deprecated, old highlight structure in the response".
 */
export const HIGHLIGHT_OFF_PARAMS = {
  highlight_fields: "none",
  highlight_full_fields: "none",
  enable_highlight_v1: false,
} as const;

interface RawHit {
  document?: Record<string, unknown>;
  highlights?: Array<{
    field?: string;
    snippet?: string;
    matched_tokens?: unknown;
  }>;
  text_match?: number;
  text_match_info?: {
    score?: string | number;
  };
}

interface RawSearchResult {
  hits?: RawHit[];
  found?: number;
  search_time_ms?: number;
  request_params?: {
    collection_name?: string;
  };
  code?: number;
  error?: string;
}

interface MultiSearchResponse {
  results: RawSearchResult[];
}

function mapHit<T>(h: RawHit): SearchHit<T> {
  return {
    document: (h.document ?? {}) as T,
    score:
      h.text_match_info && h.text_match_info.score != null
        ? Number(h.text_match_info.score) || 0
        : h.text_match ?? 0,
    highlights: (h.highlights ?? []).map((hl) => ({
      field: String(hl.field ?? ""),
      snippet: hl.snippet ?? "",
      matchedTokens: Array.isArray(hl.matched_tokens)
        ? (hl.matched_tokens as unknown[]).flat().map(String)
        : [],
    })),
  };
}

// ---------------------------------------------------------------------------
// Shared validation
// ---------------------------------------------------------------------------

/**
 * Guard against a malformed orgId being interpolated into a Typesense
 * filter_by string. orgId comes from the authenticated session context, but
 * this is defense-in-depth against injection via a crafted session value —
 * every call site that builds a filter_by string from an orgId must call
 * this first (searchAll() below, and FSRReviewQueue.tsx's searchCollection()
 * call, since searchCollection() takes an opaque pre-built filterBy string
 * rather than a typed orgId param).
 */
export function assertValidOrgId(orgId: string): void {
  if (!/^[a-zA-Z0-9_-]+$/.test(orgId)) {
    throw new Error(`Invalid orgId format: ${orgId}`);
  }
}

// ---------------------------------------------------------------------------
// Single-collection search
// ---------------------------------------------------------------------------

export async function searchCollection<T = Record<string, unknown>>(
  collection: string,
  query: string,
  options: {
    queryBy?: string;
    filterBy?: string;
    sortBy?: string;
    perPage?: number;
    page?: number;
    facetBy?: string;
    /** The per-user scoped search key (lib/search/typesenseScopedSearch#getSearchApiKey). Required — there is no browser-side default (#4031). */
    apiKey: string;
    /**
     * Comma-separated fields to return snippeted highlights for. Highlighting is
     * OFF unless a caller asks for it — see {@link HIGHLIGHT_OFF_PARAMS}. Only
     * set this if you actually render `SearchHit.highlights`.
     */
    highlightFields?: string;
  },
): Promise<SearchResult<T>> {
  // A role-gated collection is not on any key the browser holds: the server searches it after
  // checking the caller's role, with its own query_by / filters / returned fields (so queryBy,
  // filterBy, sortBy, page and facets are not honoured here: a PageSearch "page 2" over a gated collection
  // returns page 1 again, and the server caps hits per search). Known caller that passes a filterBy: the
  // Billing > Invoices search (AllInvoices), whose filter names `Balance`, `realmId` and `emailStatus`; the
  // committed qbo_invoices schema has `balance` and `emailStatus` but NO `realmId`, so that browser query
  // was not valid against the index as committed either (read from the schema, not measured live).
  if (ROLE_GATED_COLLECTIONS.has(collection)) {
    const { searchRoleGatedOne } = await import("./search/typesenseScopedSearch");
    const group = await searchRoleGatedOne(collection, query, options.perPage ?? 20);
    return {
      hits: (group?.hits ?? []) as SearchHit<T>[],
      found: group?.found ?? 0,
      page: 1,
      searchTimeMs: 0,
    };
  }

  const baseUrl = getBaseUrl();
  const {
    queryBy,
    filterBy,
    sortBy = "_text_match:desc,createdAt:desc",
    perPage = 20,
    page = 1,
    facetBy,
    apiKey,
    highlightFields,
  } = options;

  const effective = effectiveQueryBy(collection);
  const queryByFields = queryBy ?? effective.queryBy;

  const params = new URLSearchParams({
    q: query,
    query_by: queryByFields,
    query_by_weights: queryBy ? getDefaultQueryByWeights(collection) : effective.weights,
    sort_by: sortBy,
    per_page: String(perPage),
    page: String(page),
  });
  for (const [k, v] of Object.entries(HIGHLIGHT_OFF_PARAMS)) {
    params.set(k, String(v));
  }
  if (highlightFields) {
    // Snippeted (`highlight_fields`), never whole-field (`highlight_full_fields`
    // stays "none") — a snippet is bounded by highlight_affix_num_tokens, a full
    // field is not.
    params.set("highlight_fields", highlightFields);
    params.set("highlight_start_tag", "<mark>");
    params.set("highlight_end_tag", "</mark>");
    params.delete("enable_highlight_v1");
  }
  if (filterBy) params.set("filter_by", filterBy);
  if (facetBy) params.set("facet_by", facetBy);
  params.set("num_typos", "1");
  params.set("prefix", "true");

  // URLSearchParams.toString() encodes spaces as '+' (form-encoding convention).
  // Typesense's HTTP API expects standard percent-encoding (%20 for spaces).
  // Without this, filter_by operators like ' && ' become '+%26%26+' which Typesense
  // rejects as malformed (400 error).
  const queryString = params.toString().replace(/\+/g, '%20');
  const url = `${baseUrl}/collections/${encodeURIComponent(collection)}/documents/search?${queryString}`;

  const res = await fetch(url, {
    headers: { "X-TYPESENSE-API-KEY": apiKey },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    if (process.env.NODE_ENV === "development") {
      console.error("Typesense error body:", body.slice(0, 500));
    }
    // #5287: the index does not have a declared-optional field yet -> remember it and retry
    // ONCE without it. Only the default query_by is ever rewritten; a caller's own is not.
    const missing = !queryBy && res.status === 404 ? missingOptionalField(collection, body) : null;
    // Retry when THIS request asked for the missing field -- not "when nobody has recorded it
    // yet": an overlapping request may already have recorded it, and must still recover. The
    // retry rebuilds query_by without the field, so this recurses at most once.
    if (missing && queryByFields.split(",").includes(missing)) {
      missingOptionalFields.add(`${collection}.${missing}`);
      return searchCollection<T>(collection, query, options);
    }
    throw new Error(
      `Typesense search failed: ${res.status} ${res.statusText}`,
    );
  }

  const result = (await res.json()) as RawSearchResult;

  return {
    hits: (result.hits ?? []).map((h) => mapHit<T>(h)),
    found: result.found ?? 0,
    page,
    searchTimeMs: result.search_time_ms ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Multi-collection search (global Cmd+K)
// ---------------------------------------------------------------------------

// buildContactsAwareFilter lives in @officehub/shared (one definition, shared with the
// golden-set runner in functions/src/search/goldenRun.ts); re-exported for existing importers.
export { buildContactsAwareFilter };

/** A single collection's group in a multi_search response. */
type SearchGroup = MultiSearchResult["results"][number];

/**
 * One collection's raw Typesense hits as a result group — the shape Cmd+K renders.
 * Used for groups the server returns (search_searchRestricted, #5550).
 */
export function toSearchGroup(collection: string, rawHits: unknown[], found: number): SearchGroup {
  return {
    collection,
    hits: (rawHits as RawHit[]).map((h) => mapHit<Record<string, unknown>>(h)),
    found,
  };
}

/** Build the multi_search entry for one collection. */
function buildSearchEntry(
  collection: string,
  query: string,
  orgId: string,
  canViewCandidates: boolean,
  perCollection: number,
): Record<string, unknown> {
  // Project down to the fields the palette actually renders. Absent collection
  // => no projection (full documents, today's behavior) rather than a blank row.
  const includeFields = effectiveIncludeFields(collection);
  const effective = effectiveQueryBy(collection);
  return {
    collection,
    q: query,
    ...(includeFields ? { include_fields: includeFields } : {}),
    query_by: effective.queryBy,
    query_by_weights: effective.weights,
    filter_by: buildContactsAwareFilter(collection, orgId, canViewCandidates),
    ...CMDK_SEARCH_OPTIONS,
    per_page: perCollection,
    ...HIGHLIGHT_OFF_PARAMS,
  };
}

/**
 * Issue one multi_search request with a specific API key and map the response
 * into per-collection groups (index-aligned with `collections`). Collections
 * that returned a per-result error (e.g. a 404 mid-reindex) map to an empty
 * group rather than failing the whole batch. Throws on a transport/auth-level
 * failure (`!res.ok`).
 */
// DO NOT add Typesense's `use_cache` to this request (or to searchCollection's).
// It looks like an obvious win for a search-as-you-type UI, and it is a
// cross-tenant data leak here. Typesense keys its response cache on
// `hash_request()` = route hash + request body + the values of the URL query
// params (`src/core_api.cpp`). The API key travels in the `X-TYPESENSE-API-KEY`
// header, which is explicitly excluded from that param map, and the filters
// embedded in a *scoped* key are carried in a separate `embedded_params_vec`
// that never reaches the hash (`src/http_server.cpp`). Every org therefore sends
// a byte-identical body for the same query text, and the one thing that
// distinguishes them — the signed `organizationId:=<org>` filter inside the
// scoped key (#2404) — is invisible to the cache key. Org A searching "smith"
// would be served org B's cached hits.
async function runMultiSearch(
  baseUrl: string,
  apiKey: string,
  searches: Record<string, unknown>[],
  collections: string[],
): Promise<{ groups: SearchGroup[]; searchTimeMs: number; errors: (string | null)[] }> {
  const res = await fetch(`${baseUrl}/multi_search`, {
    method: "POST",
    headers: {
      "X-TYPESENSE-API-KEY": apiKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ searches }),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    if (process.env.NODE_ENV === "development") {
      console.error("Typesense error body:", body.slice(0, 500));
    }
    throw new Error(
      `Typesense multi-search failed: ${res.status} ${res.statusText}`,
    );
  }

  const response = (await res.json()) as MultiSearchResponse;
  let searchTimeMs = 0;
  const errors: (string | null)[] = [];

  const groups = (response.results ?? []).map((r, i) => {
    // Skip collections that returned errors (e.g., 404 during reindex)
    if (r.error || (r.code !== undefined && r.code !== 200)) {
      errors[i] = String(r.error ?? `code ${r.code}`);
      return { collection: collections[i], hits: [], found: 0 };
    }
    errors[i] = null;
    searchTimeMs = Math.max(searchTimeMs, r.search_time_ms ?? 0);
    return {
      collection: collections[i],
      hits: (r.hits ?? []).map((h) => mapHit<Record<string, unknown>>(h)),
      found: r.found ?? 0,
    };
  });

  return { groups, searchTimeMs, errors };
}

/**
 * #5287: re-run, once, every group whose error names a declared-optional field the index does
 * not have yet, without that field. Groups that failed for any other reason stay empty, as before.
 */
async function retryMissingOptionalFields(
  baseUrl: string,
  apiKey: string,
  batch: { groups: SearchGroup[]; searchTimeMs: number; errors: (string | null)[] },
  sent: Record<string, unknown>[],
  build: (collection: string) => Record<string, unknown>,
): Promise<{ groups: SearchGroup[]; searchTimeMs: number }> {
  const retry: string[] = [];
  batch.groups.forEach((g, i) => {
    const missing = missingOptionalField(g.collection, batch.errors[i] ?? undefined);
    // Retry when the search WE SENT asked for the field (see searchCollection): overlapping
    // Cmd+K keystrokes may have recorded it already, and each must still recover its group.
    const asked = String(sent[i]?.query_by ?? "").split(",").includes(missing ?? "");
    if (missing && asked) {
      missingOptionalFields.add(`${g.collection}.${missing}`);
      retry.push(g.collection);
    }
  });
  if (retry.length === 0) return { groups: batch.groups, searchTimeMs: batch.searchTimeMs };
  let again: { groups: SearchGroup[]; searchTimeMs: number };
  try {
    again = await runMultiSearch(baseUrl, apiKey, retry.map(build), retry);
  } catch (err) {
    // A failed retry must not take the rest of Cmd+K down with it: keep every other group,
    // and the retried groups stay empty (what the user would have seen before this fallback).
    if (process.env.NODE_ENV === "development") console.error("Optional-field retry failed:", err);
    return { groups: batch.groups, searchTimeMs: batch.searchTimeMs };
  }
  const byCollection = new Map(again.groups.map((g) => [g.collection, g]));
  return {
    groups: batch.groups.map((g) => byCollection.get(g.collection) ?? g),
    searchTimeMs: Math.max(batch.searchTimeMs, again.searchTimeMs),
  };
}

export async function searchAll(
  query: string,
  orgId: string,
  options: {
    collections?: string[];
    perCollection?: number;
    /**
     * Whether the caller may see recruiting candidates (has P14 access). When
     * false (the safe default), candidate contacts are filtered out of the
     * `contacts` results. See {@link buildContactsAwareFilter}.
     */
    canViewCandidates?: boolean;
    /**
     * The per-org *scoped* Typesense key (#2404), minted by
     * `search_getScopedSearchKey` via lib/search/typesenseScopedSearch#getScopedSearchKey.
     * Its embedded `organizationId:=<org>` filter is signed into the key and ANDed
     * into every query. REQUIRED: the browser has no other key (#4031), so a
     * missing key fails the search closed rather than widening it. Covers every
     * collection on the browser key. `assets` is not on it since #6079: Cmd+K reaches it through
     * search_searchRestricted (see ROLE_GATED_COLLECTIONS).
     */
    scopedApiKey: string;
  },
): Promise<MultiSearchResult> {
  const baseUrl = getBaseUrl();
  // A role-gated collection (contacts, assets, QuickBooks, ...) is not on the key searchAll holds: the server
  // searches it (searchRoleGated). One that reaches here is dropped, never sent to the browser key (#3228).
  const collections = (options.collections ?? Object.keys(COLLECTION_META)).filter(
    (c) => !ROLE_GATED_COLLECTIONS.has(c),
  );
  const perCollection = options.perCollection ?? 5;
  const canViewCandidates = options.canViewCandidates ?? false;
  const scopedApiKey = options.scopedApiKey;

  assertValidOrgId(orgId);

  // Main batch key: the per-org scoped key when the caller supplied one (org
  // isolation is then signed into the key and cannot be widened client-side —
  // this is what lets the broad key eventually be revoked), else the broad key
  // with the client-side org filter (prior behavior). `??` is lazy, so the broad
  // key env is only read when no scoped key was supplied.
  if (!scopedApiKey) {
    throw new Error("Search is unavailable: no scoped search key.");
  }
  const mainKey = scopedApiKey;

  const mainCollections = collections;
  const mainSearches = mainCollections.map((c) =>
    buildSearchEntry(c, query, orgId, canViewCandidates, perCollection),
  );

  // Main batch — errors here fail the whole search, as before. Skip the request
  // entirely when the main batch is empty (e.g. only role-gated collections were requested), so
  // we never POST an empty `searches` array.
  const buildEntry = (c: string) => buildSearchEntry(c, query, orgId, canViewCandidates, perCollection);
  const mainPromise: Promise<{ groups: SearchGroup[]; searchTimeMs: number }> =
    mainCollections.length > 0
      ? runMultiSearch(baseUrl, mainKey, mainSearches, mainCollections).then((batch) =>
          retryMissingOptionalFields(baseUrl, mainKey, batch, mainSearches, buildEntry))
      : Promise.resolve({ groups: [], searchTimeMs: 0 });

  const main = await mainPromise;
  const byCollection = new Map<string, SearchGroup>();
  for (const g of main.groups) {
    byCollection.set(g.collection, g);
  }
  const ordered = collections
    .map((c) => byCollection.get(c))
    .filter((g): g is SearchGroup => g !== undefined);

  const totalFound = ordered.reduce((sum, g) => sum + g.found, 0);

  return {
    results: ordered.filter((r) => r.found > 0),
    totalFound,
    searchTimeMs: main.searchTimeMs,
  };
}

// ---------------------------------------------------------------------------
// Default query_by fields per collection
// ---------------------------------------------------------------------------

export function getDefaultQueryBy(collection: string): string {
  const QUERY_BY: Record<string, string> = {
    call_logs: "callerName,callerPhone,callNotes,address,callerEmail,statusNotes",
    contacts: "fullName,email,phone,mobilePhone,companyName,title",
    email_messages: "subject,fromEmail,fromName,toText,snippet",
    projects: "clientName,installId,address,city",
    leads: "companyName,opportunityName,contactName,email,phone,address",
    equipment: "equipmentId,customerName,siteName,equipmentType,generatorMake,generatorModel,generatorSerialNumber,engineMake,engineModel,address",
    support_cases: "subject,description,caseNumber,requesterName,requesterEmail",
    service_tickets: "generatorId,customerName,shortDescription,appSheetServiceId,address,technician",
    fluid_samples: "sampleId,sampleIdNormalized,serviceTicketId,unitName,customerName,technician,generatorId",
    quotes: "quoteNumber,subject,customerName,contactName,scopeOfWork",
    employees: "fullName,firstName,lastName,email,phone,title,department,employeeNumber",
    assets: "name,assetId,vin,licensePlate,assignedToName",
    form_submissions: "templateName,customerName,submittedByName,serviceTicketId,scope,technicianEmail,jobNumber,genSerialNumber,equipmentAddress,unitName,branch,genMake,genModel",
    price_book_items: "description,partNumber,supplier,manufacturer",
    service_parts_list: "partNumber,partDescription,appSheetPartId,binLocation,category",
    qbo_customers: "displayName,companyName,fullyQualifiedName,email,phone,mobile,billAddress",
    qbo_invoices: "docNumber,customerName,billEmail",
    qbo_bills: "docNumber,vendorName,privateNote",
    qbo_estimates: "docNumber,customerName",
    qbo_vendors: "displayName,printOnCheckName",
    issue_reports: "issueCode,title,description,reportedByName,reportedByEmail",
    investors: "name,email,phone,entityName",
  };

  return QUERY_BY[collection] ?? "id";
}

export function getDefaultQueryByWeights(collection: string): string {
  const WEIGHTS: Record<string, string> = {
    // call_logs: callerName,callerPhone,callNotes,address,callerEmail,statusNotes
    call_logs:          "4,4,1,1,2,1",
    // contacts: fullName,email,phone,mobilePhone,companyName,title
    // companyName boosted to 4 — CRM reps search by company as often as by person name
    contacts:           "4,2,2,2,4,1",
    // email_messages: subject,fromEmail,fromName,toText,snippet
    email_messages:     "4,3,3,2,1",
    // projects: clientName,installId,address,city
    // installId weighted equal to clientName — it's a primary lookup key.
    // salesPerson dropped from search (kept as a facet only); a project should
    // not surface globally when a user types a salesperson's name.
    projects:           "4,4,1,1",
    // leads: companyName,opportunityName,contactName,email,phone,address
    leads:              "4,4,4,2,2,1",
    // equipment: equipmentId,customerName,siteName,equipmentType,generatorMake,generatorModel,generatorSerialNumber,engineMake,engineModel,address
    // equipmentId boosted to 6 (intentionally above the file-wide max of 4) —
    // the Generator ID (e.g. G2016-0485) is the primary lookup key techs paste into Cmd+K
    equipment:          "6,4,2,2,4,4,4,4,4,1",
    // support_cases: subject,description,caseNumber,requesterName,requesterEmail
    support_cases:      "2,1,4,4,2",
    // service_tickets: generatorId,customerName,shortDescription,appSheetServiceId,address,technician
    // generatorId boosted to 7 (above appSheetServiceId's 6): pasting a Generator ID is how
    // techs pull up a unit's ticket history, so it must outrank the ticket-number match
    // (today "G2016-0485" only fuzzy-matches ticket numbers ending "-085"). generatorId is an
    // OPTIONAL query field -- see OPTIONAL_QUERY_FIELDS. shortDescription is free-text, so 1.
    service_tickets:    "7,4,1,6,1,2",
    // fluid_samples: sampleId,sampleIdNormalized,serviceTicketId,unitName,customerName,technician,generatorId
    // The bottle's sample ID is the lookup key (typed with or without its dash), so both
    // forms get 6, like the ticket # above; the ticket # itself is next.
    fluid_samples:      "6,6,4,3,3,1,2",
    // quotes: quoteNumber,subject,customerName,contactName,scopeOfWork
    quotes:             "4,2,4,2,1",
    // employees: fullName,firstName,lastName,email,phone,title,department,employeeNumber
    employees:          "4,4,4,2,2,1,1,4",
    // assets: name,assetId,vin,licensePlate,assignedToName
    assets:             "4,4,4,4,2",
    // form_submissions: templateName,customerName,submittedByName,serviceTicketId,scope,technicianEmail,
    //                   jobNumber,genSerialNumber,equipmentAddress,unitName,branch,genMake,genModel
    // serviceTicketId + jobNumber boosted to 4 (primary lookup keys); serial/unit at 3;
    // equipmentAddress/make/model at 2; branch/scope low (broad, noisy)
    form_submissions:   "2,4,2,4,1,2,4,3,2,3,1,2,2",
    // price_book_items: description,partNumber,supplier,manufacturer
    price_book_items:   "2,4,2,2",
    // service_parts_list: partNumber,partDescription,appSheetPartId,binLocation,category
    // binLocation boosted to 4 — warehouse staff scan bin labels; partDescription lowered
    // to 1 since it's long free-text that adds noise relative to structured identifiers
    service_parts_list: "4,1,4,4,1",
    // qbo_customers: displayName,companyName,fullyQualifiedName,email,phone,mobile,billAddress
    qbo_customers:      "4,4,2,2,2,2,1",
    // qbo_invoices: docNumber,customerName,billEmail
    qbo_invoices:       "4,4,2",
    // qbo_bills: docNumber,vendorName,privateNote
    qbo_bills:          "4,4,1",
    // qbo_estimates: docNumber,customerName
    qbo_estimates:      "4,4",
    // qbo_vendors: displayName,printOnCheckName
    qbo_vendors:        "4,2",
    // issue_reports: issueCode,title,description,reportedByName,reportedByEmail
    issue_reports:      "4,4,1,4,2",
    // investors: name,email,phone,entityName
    investors:          "4,2,2,4",
  };
  return WEIGHTS[collection] ?? "1";
}

// ---------------------------------------------------------------------------
// Query intent detection
// ---------------------------------------------------------------------------

export type QueryIntent = {
  boostedCollections: string[];
  confidence: "high" | "low";
  /**
   * TRUE when boostedCollections' ORDER is authoritative, not just membership:
   * the first collection always renders first, whatever the BM25 scores say.
   */
  ordered?: boolean;
};

/**
 * A Generator ID (appsheet_generators "Generator ID" = equipment.equipmentId), whole
 * or partly typed, any case. Shapes measured 2026-09-29 over all 3,288 generators:
 * G + 4 digits + "-" + 4-5 alphanumerics (G2016-0485, G2406-A4D8, G2406-6021C) and
 * the legacy G + 2 letters + 2 digits + 2 letters (GAB31CC). Matched 3,287 of 3,288
 * (the miss is the TEST-E2E-GEN fixture), 0 of 21,687 Service IDs, 0 of 3,693 part
 * numbers.
 */
const GENERATOR_ID_PATTERN = /^g(?:\d{4}(?:-[a-z0-9]{0,5})?|[a-z]{2}\d{2}[a-z]{2})$/i;

/**
 * The last three characters of a Service ID ("57J"): how dispatchers quote a ticket (Nick
 * 2026-10-01). Must mix letters and digits, so "123" stays an invoice number and "abc" a word.
 *
 * Known trade-off, measured read-only 2026-10-02 (controls: classifier pos/neg cases; every count
 * equals the server count): 18,241 of 21,738 Service IDs (84%) end in such a tail, but so do
 * 1,337 of 1,361 bin locations EXACTLY ("A12"), 10 of 3,697 part numbers exactly and 1,629 (44%)
 * by their first three characters, and 38 of 57 asset IDs by their first three; 0 of 5 employee
 * numbers; price book NOT measured (its source collection held 0 docs). So this rule only RANKS:
 * tickets first, then parts and assets, all in the top section; every other collection is still
 * searched and shown under "Other results" (lib/search/ranking.ts).
 */
const SERVICE_ID_TAIL_PATTERN = /^(?=[a-z0-9]*\d)(?=[a-z0-9]*[a-z])[a-z0-9]{3}$/i;

const INTENT_RULES: Array<{
  pattern: RegExp;
  collections: string[];
  confidence: "high" | "low";
  ordered?: boolean;
}> = [
  {
    // A pasted Generator ID means "take me to that generator" from any module: the
    // generator record first, then its tickets and fluid samples. The parts list is
    // deliberately NOT boosted — a part matching the ID's text is never the answer.
    pattern: GENERATOR_ID_PATTERN,
    collections: ["equipment", "service_tickets", "fluid_samples"],
    confidence: "high",
    ordered: true,
  },
  {
    // A Service ID: the ticket first, from any module. Without this, from Home the tickets were in
    // the SECOND search wave and a Service ID showed other records first.
    pattern: SERVICE_ID_PATTERN,
    collections: ["service_tickets"],
    confidence: "high",
    ordered: true,
  },
  {
    // A 3-character tail like "57J": tickets first, but parts (bin locations!) and assets share
    // the shape, so they stay in the top section right under the tickets. See the trade-off above.
    pattern: SERVICE_ID_TAIL_PATTERN,
    collections: ["service_tickets", "service_parts_list", "assets"],
    confidence: "high",
    ordered: true,
  },
  {
    pattern: /^\(?\d{3}\)?[\s-]?\d{3}/,
    collections: ["contacts", "call_logs", "qbo_customers"],
    confidence: "high",
  },
  {
    pattern: /@/,
    collections: ["contacts", "employees", "leads"],
    confidence: "high",
  },
  {
    // Matches 3-6 digit standalone numbers as invoice/quote doc numbers.
    // Known trade-off: also matches ZIP codes (e.g., "95014") — acceptable
    // since invoice lookup is the higher-frequency use case in this context.
    pattern: /^\d{3,6}$/,
    collections: ["qbo_invoices", "qbo_bills", "quotes"],
    confidence: "high",
  },
  {
    pattern: /^[A-Z0-9]{2,4}[-/]\d+/,
    collections: ["equipment", "assets", "price_book_items"],
    confidence: "high",
  },
  {
    pattern: /^[A-Z][a-z]+ [A-Z][a-z]/,
    collections: ["contacts", "employees", "leads", "qbo_customers"],
    confidence: "low",
  },
];

export function detectQueryIntent(query: string): QueryIntent {
  const trimmed = query.trim();
  if (trimmed.length < 2) return { boostedCollections: [], confidence: "low" };

  for (const rule of INTENT_RULES) {
    if (rule.pattern.test(trimmed)) {
      return rule.ordered ?
        { boostedCollections: rule.collections, confidence: rule.confidence, ordered: true } :
        { boostedCollections: rule.collections, confidence: rule.confidence };
    }
  }

  return { boostedCollections: [], confidence: "low" };
}

// ---------------------------------------------------------------------------
// Optional query fields (#5287)
// ---------------------------------------------------------------------------

/**
 * Query fields a collection MAY search but whose LIVE index might not have yet. The Typesense
 * schema only gains a field when the nightly reindex rebuilds the collection, and Typesense
 * rejects an unknown query_by field with a 404 ("Could not find a field named `x` in the
 * schema."). In multi_search that 404 maps the whole group to EMPTY (runMultiSearch), so
 * shipping a field ahead of the index would silently blank that collection in Cmd+K. Instead a
 * missing optional field is detected from that error, remembered for this page load, and the
 * search re-run once without it -- today's fields, today's weights.
 */
export const OPTIONAL_QUERY_FIELDS: Readonly<Record<string, readonly string[]>> = {
  service_tickets: ["generatorId"],
};

const missingOptionalFields = new Set<string>();

/** Test-only: forget which optional fields were found missing. */
export function resetMissingOptionalFieldsForTest(): void {
  missingOptionalFields.clear();
}

/** The declared-optional field this Typesense error says is missing, or null. */
export function missingOptionalField(collection: string, errorText: string | undefined): string | null {
  const m = /field named [`'"]?([A-Za-z0-9_.]+)[`'"]?/i.exec(errorText ?? "");
  if (!m) return null;
  return (OPTIONAL_QUERY_FIELDS[collection] ?? []).includes(m[1]) ? m[1] : null;
}

function isKnownMissing(collection: string, field: string): boolean {
  return missingOptionalFields.has(`${collection}.${field}`);
}

/** query_by + weights with any known-missing optional field removed; weights stay aligned. */
export function effectiveQueryBy(collection: string): { queryBy: string; weights: string } {
  const fields = getDefaultQueryBy(collection).split(",");
  const weights = getDefaultQueryByWeights(collection).split(",");
  const keep = fields.map((f) => !isKnownMissing(collection, f));
  if (keep.every(Boolean)) return { queryBy: fields.join(","), weights: weights.join(",") };
  return {
    queryBy: fields.filter((_, i) => keep[i]).join(","),
    weights: weights.length === fields.length ? weights.filter((_, i) => keep[i]).join(",") : weights.join(","),
  };
}

/** include_fields for the palette, minus any known-missing optional field. */
function effectiveIncludeFields(collection: string): string | undefined {
  const include = COLLECTION_INCLUDE_FIELDS[collection];
  if (!include) return include;
  return include.split(",").filter((f) => !isKnownMissing(collection, f)).join(",");
}
